import React from "react";
import { useAuth, useExcursion } from "../api";
import { useHRData } from "./hrData";
import useAppSettings from "./appSettings";
import { toLocalDateKey } from "utils/dateTime";
import type {
  PlanningCalendarScope,
  PlanningCategoryFilter,
} from "components/planning/gerenalPlanning/display/PlanningFilters";
import {
  allowedPlanningCalendarScopes,
  coalescePlanningCalendarScope,
} from "components/planning/gerenalPlanning/display/PlanningFilters";
import {
  filterBookingsForPlanningCalendarScope,
  filterLeavesForPlanningCalendarScope,
} from "utils/planningLeaveHelpers";
import { usePlanningPermissions } from "components/planning/gerenalPlanning/hooks/usePlanningPermissions";
import { usePlanningCalendar } from "components/planning/gerenalPlanning/hooks/usePlanningCalendar";
import type { PlanningCalendarViewMode } from "components/planning/gerenalPlanning/hooks/usePlanningCalendar";
import { usePlanningEmployees } from "components/planning/gerenalPlanning/hooks/usePlanningEmployees";
import { usePlanningData as usePlanningDataFetch } from "components/planning/gerenalPlanning/hooks/usePlanningData";
import { usePlanningDayLookups } from "components/planning/gerenalPlanning/hooks/usePlanningDayLookups";
import type { PlanningFilterMenuProps } from "components/planning/gerenalPlanning/display/PlanningFilters";
import type { PlanningCalendarGridProps } from "components/planning/gerenalPlanning/display/PlanningCalendarGrid";
import type { DayCellAction } from "components/common/PlanningCalendar";

export type PlanningModuleLeaveForm = {
  canCreate: boolean;
  open: boolean;
  initialDateKey: string | null;
  onClose: () => void;
  onSave: () => void;
  allowedTypeIds: number[] | undefined;
  restrictTypeSelection: boolean;
};

export type PlanningModuleContextValue = {
  canAccessPlanning: boolean;
  loading: boolean;

  periodTitle: string;
  viewMode: PlanningCalendarViewMode;
  setViewMode: (mode: PlanningCalendarViewMode) => void;
  handlePrev: () => void;
  handleNext: () => void;
  handleToday: () => void;

  filterMenuProps: PlanningFilterMenuProps;
  gridProps: PlanningCalendarGridProps;
  leaveForm: PlanningModuleLeaveForm;
};

const PlanningModuleContext = React.createContext<PlanningModuleContextValue | undefined>(
  undefined,
);

export function PlanningModuleProvider({ children }: { children: React.ReactNode }) {
  const { user, getUserName } = useAuth();
  const { updateExcursionBookingStatus } = useExcursion();
  const { typesMap, leaveTypes } = useHRData();
  const { config } = useAppSettings();

  const perms = usePlanningPermissions();
  const calendar = usePlanningCalendar();
  const { pickerUsers: employees, userIsEmployeeById: planningUserEmployeeMap } =
    usePlanningEmployees(
      perms.canAccessPlanning,
      perms.canShowPlanningFilters,
      perms.hasPlanningAllView,
    );

  const [selectedUserId, setSelectedUserId] = React.useState(-1);
  const [categoryFilter, setCategoryFilter] = React.useState<PlanningCategoryFilter>("all");
  const [leaveFormOpen, setLeaveFormOpen] = React.useState(false);
  const [leaveFormInitialDateKey, setLeaveFormInitialDateKey] = React.useState<string | null>(null);

  const allowedPlanningScopes = React.useMemo(
    () =>
      allowedPlanningCalendarScopes({
        hasPlanningAllView: perms.hasPlanningAllView,
        hasPlanningCompanyView: perms.hasPlanningCompanyView,
        hasPlanningMeView: perms.hasPlanningMeView,
      }),
    [perms.hasPlanningAllView, perms.hasPlanningCompanyView, perms.hasPlanningMeView],
  );

  const [planningScope, setPlanningScope] = React.useState<PlanningCalendarScope>("all");

  React.useEffect(() => {
    setPlanningScope((prev) => coalescePlanningCalendarScope(allowedPlanningScopes, prev));
  }, [allowedPlanningScopes]);

  React.useEffect(() => {
    setSelectedUserId(-1);
  }, [planningScope]);

  React.useEffect(() => {
    if (!perms.canShowPlanningFilters) setSelectedUserId(-1);
  }, [perms.canShowPlanningFilters]);

  const { requests, bookings, loading, loadPlanningData } = usePlanningDataFetch(
    perms.canAccessPlanning,
    user,
    calendar.viewMode,
    calendar.currentDate,
    perms.shouldLoadExcursionBookings,
  );

  const getUserNameRef = React.useRef(getUserName);
  getUserNameRef.current = getUserName;
  const stableGetUserName = React.useCallback((id: number) => getUserNameRef.current(id), []);

  const updateBookingStatusRef = React.useRef(updateExcursionBookingStatus);
  updateBookingStatusRef.current = updateExcursionBookingStatus;

  const loadPlanningDataRef = React.useRef(loadPlanningData);
  loadPlanningDataRef.current = loadPlanningData;

  const handlePrev = React.useCallback(
    () => calendar.navigatePeriod("prev"),
    [calendar.navigatePeriod],
  );
  const handleNext = React.useCallback(
    () => calendar.navigatePeriod("next"),
    [calendar.navigatePeriod],
  );
  const handleToday = React.useCallback(
    () => calendar.setCurrentDate(new Date()),
    [calendar.setCurrentDate],
  );

  const handleLeaveFormClose = React.useCallback(() => {
    setLeaveFormOpen(false);
    setLeaveFormInitialDateKey(null);
  }, []);

  const handleLeaveFormSave = React.useCallback(() => void loadPlanningDataRef.current(), []);

  const handleBookingStatusChange = React.useCallback(
    async (bookingId: number, status: "validated" | "rejected") => {
      await updateBookingStatusRef.current(bookingId, status);
      await loadPlanningDataRef.current();
    },
    [],
  );

  const handleDayCellClick = React.useCallback((d: Date) => {
    setLeaveFormInitialDateKey(toLocalDateKey(d));
    setLeaveFormOpen(true);
  }, []);

  const scopedRequests = React.useMemo(
    () =>
      filterLeavesForPlanningCalendarScope(
        requests,
        planningScope,
        user?.id,
        user?.company_id,
        planningUserEmployeeMap,
      ),
    [requests, planningScope, user?.id, user?.company_id, planningUserEmployeeMap],
  );

  const scopedBookings = React.useMemo(
    () =>
      filterBookingsForPlanningCalendarScope(
        bookings,
        planningScope,
        user?.id,
        user?.company_id,
        planningUserEmployeeMap,
      ),
    [bookings, planningScope, user?.id, user?.company_id, planningUserEmployeeMap],
  );

  const filteredRequests = React.useMemo(
    () => (categoryFilter === "bookings" ? [] : scopedRequests),
    [categoryFilter, scopedRequests],
  );

  const filteredBookings = React.useMemo(
    () => (categoryFilter === "leaves" ? [] : scopedBookings),
    [categoryFilter, scopedBookings],
  );

  const { getRequestsForDay, getBookingsForDay } = usePlanningDayLookups(
    filteredRequests,
    filteredBookings,
    perms.canShowPlanningFilters,
    selectedUserId,
  );

  const employeesForFilter = React.useMemo(
    () =>
      planningScope !== "company"
        ? employees
        : employees.filter((row) => row.role?.is_employee === true),
    [employees, planningScope],
  );

  const showPlanningEmployeeFilter = perms.canShowPlanningFilters && planningScope !== "me";

  const filterMenuProps = React.useMemo<PlanningFilterMenuProps>(
    () => ({
      employees: employeesForFilter,
      selectedUserId,
      onUserChange: setSelectedUserId,
      allowedPlanningScopes,
      planningScope,
      onPlanningScopeChange: setPlanningScope,
      showEmployeeFilter: showPlanningEmployeeFilter,
      categoryFilter,
      onCategoryFilterChange: setCategoryFilter,
    }),
    [
      employeesForFilter,
      selectedUserId,
      allowedPlanningScopes,
      planningScope,
      showPlanningEmployeeFilter,
      categoryFilter,
    ],
  );

  const dayCellActions = React.useMemo<DayCellAction[] | undefined>(() => {
    if (!perms.canCreateLeaveFromPlanning) return undefined;
    return [
      { key: "leave", label: "Congés", emoji: "\u{1F3D6}\u{FE0F}", onAction: handleDayCellClick },
    ];
  }, [perms.canCreateLeaveFromPlanning, handleDayCellClick]);

  const gridProps = React.useMemo<PlanningCalendarGridProps>(
    () => ({
      viewMode: calendar.viewMode,
      days: calendar.days,
      monthGridOffset: calendar.monthGridOffset,
      getRequestsForDay,
      typesMap,
      getBookingsForDay,
      getUserName: stableGetUserName,
      currentUserId: user?.id,
      viewerTenantCompanyId: user?.company_id,
      companyTimeZone: config?.TimeZone,
      leaveTypes,
      viewerHasPlanningMeView: perms.hasPlanningMeView,
      showMeViewCompanyCartouche: perms.showMeViewCompanyCartouche,
      canValidateAnyBooking: perms.canValidateAnyBooking,
      onBookingStatusChange: handleBookingStatusChange,
      dayCellActions,
      autoScrollToToday: true,
    }),
    [
      calendar.viewMode,
      calendar.days,
      calendar.monthGridOffset,
      getRequestsForDay,
      typesMap,
      getBookingsForDay,
      stableGetUserName,
      user?.id,
      user?.company_id,
      config?.TimeZone,
      leaveTypes,
      perms.hasPlanningMeView,
      perms.showMeViewCompanyCartouche,
      perms.canValidateAnyBooking,
      handleBookingStatusChange,
      dayCellActions,
    ],
  );

  const allowedLeaveTypeIds = React.useMemo(
    () =>
      user?.Role?.is_employee === false && perms.globalUnavailabilityLeaveType
        ? [perms.globalUnavailabilityLeaveType.ID]
        : undefined,
    [user?.Role?.is_employee, perms.globalUnavailabilityLeaveType],
  );

  const leaveForm = React.useMemo<PlanningModuleLeaveForm>(
    () => ({
      canCreate: perms.canCreateLeaveFromPlanning,
      open: leaveFormOpen,
      initialDateKey: leaveFormInitialDateKey,
      onClose: handleLeaveFormClose,
      onSave: handleLeaveFormSave,
      allowedTypeIds: allowedLeaveTypeIds,
      restrictTypeSelection:
        user?.Role?.is_employee === false && perms.globalUnavailabilityLeaveType != null,
    }),
    [
      perms.canCreateLeaveFromPlanning,
      leaveFormOpen,
      leaveFormInitialDateKey,
      handleLeaveFormClose,
      handleLeaveFormSave,
      allowedLeaveTypeIds,
      user?.Role?.is_employee,
      perms.globalUnavailabilityLeaveType,
    ],
  );

  const periodTitle = calendar.formatPeriodTitle();

  const value = React.useMemo<PlanningModuleContextValue>(
    () => ({
      canAccessPlanning: perms.canAccessPlanning,
      loading,
      periodTitle,
      viewMode: calendar.viewMode,
      setViewMode: calendar.setViewMode,
      handlePrev,
      handleNext,
      handleToday,
      filterMenuProps,
      gridProps,
      leaveForm,
    }),
    [
      perms.canAccessPlanning,
      loading,
      periodTitle,
      calendar.viewMode,
      calendar.setViewMode,
      handlePrev,
      handleNext,
      handleToday,
      filterMenuProps,
      gridProps,
      leaveForm,
    ],
  );

  return <PlanningModuleContext.Provider value={value}>{children}</PlanningModuleContext.Provider>;
}

export function usePlanningModule(): PlanningModuleContextValue {
  const ctx = React.useContext(PlanningModuleContext);
  if (!ctx) throw new Error("usePlanningModule must be used within PlanningModuleProvider");
  return ctx;
}
